import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Slider,
  Grid
} from '@mui/material';
import api from '../../services/api';

/**
 * Component for managing message archival settings in the admin dashboard.
 * This allows configuration of message history limits and archival buffer size.
 */
const MessageArchivalSettings = () => {
  const [settings, setSettings] = useState({
    messageHistoryLimit: 500,
    messageArchiveBufferSize: 100,
    archivingEnabled: true
  });
  
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Load settings when component mounts
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const settings = await api.getPlatformSettings();
        
        // Extract message archival settings with proper null checking
        // The API returns the settings object directly, not nested in response.data
        let messageArchivalSettings = {
          messageHistoryLimit: 500,
          messageArchiveBufferSize: 100,
          archivingEnabled: true
        };
        
        if (settings && settings.messageArchivalSettings) {
          messageArchivalSettings = {
            ...messageArchivalSettings,
            ...settings.messageArchivalSettings
          };
        } else {
          console.log('No existing message archival settings found, using defaults');
        }
        
        setSettings(messageArchivalSettings);
        setOriginalSettings(messageArchivalSettings);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching message archival settings:', err);
        setError('Failed to load settings. Please try again.');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Convert string values to numbers where needed
      const formattedSettings = {
        ...settings,
        messageHistoryLimit: parseInt(settings.messageHistoryLimit, 10),
        messageArchiveBufferSize: parseInt(settings.messageArchiveBufferSize, 10)
      };
      
      // Update platform settings with new message archival settings
      await api.updatePlatformSettings({
        messageArchivalSettings: formattedSettings
      });
      
      setSuccess(true);
      setOriginalSettings(formattedSettings);
      setSaving(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving message archival settings:', err);
      setError('Failed to save settings. Please try again.');
      setSaving(false);
    }
  };
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle slider changes
  const handleSliderChange = (name) => (_, newValue) => {
    setSettings({
      ...settings,
      [name]: newValue
    });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Message Archival Settings
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure how the system manages conversation message history and archives older messages to maintain performance.
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully!
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.archivingEnabled}
                onChange={handleInputChange}
                name="archivingEnabled"
                color="primary"
              />
            }
            label="Enable Message Archiving"
            sx={{ mb: 3, display: 'block' }}
          />
          
          <Typography gutterBottom>
            Message History Limit: {settings.messageHistoryLimit}
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs>
              <Slider
                value={parseInt(settings.messageHistoryLimit, 10)}
                onChange={handleSliderChange('messageHistoryLimit')}
                onChangeCommitted={() => {}}
                min={50}
                max={2000}
                step={50}
                disabled={!settings.archivingEnabled}
                marks={[
                  { value: 50, label: '50' },
                  { value: 500, label: '500' },
                  { value: 1000, label: '1000' },
                  { value: 2000, label: '2000' }
                ]}
              />
            </Grid>
            <Grid item>
              <TextField
                value={settings.messageHistoryLimit}
                onChange={handleInputChange}
                name="messageHistoryLimit"
                type="number"
                inputProps={{ min: 50, max: 2000 }}
                disabled={!settings.archivingEnabled}
                variant="outlined"
                size="small"
                sx={{ width: '100px' }}
              />
            </Grid>
          </Grid>
          
          <Typography gutterBottom>
            Archive Buffer Size: {settings.messageArchiveBufferSize}
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
            <Grid item xs>
              <Slider
                value={parseInt(settings.messageArchiveBufferSize, 10)}
                onChange={handleSliderChange('messageArchiveBufferSize')}
                onChangeCommitted={() => {}}
                min={10}
                max={500}
                step={10}
                disabled={!settings.archivingEnabled}
                marks={[
                  { value: 10, label: '10' },
                  { value: 100, label: '100' },
                  { value: 250, label: '250' },
                  { value: 500, label: '500' }
                ]}
              />
            </Grid>
            <Grid item>
              <TextField
                value={settings.messageArchiveBufferSize}
                onChange={handleInputChange}
                name="messageArchiveBufferSize"
                type="number"
                inputProps={{ min: 10, max: 500 }}
                disabled={!settings.archivingEnabled}
                variant="outlined"
                size="small"
                sx={{ width: '100px' }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setSettings(originalSettings)}
              disabled={saving || JSON.stringify(settings) === JSON.stringify(originalSettings)}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || JSON.stringify(settings) === JSON.stringify(originalSettings)}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Box>
        </form>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            What do these settings do?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Message History Limit:</strong> The maximum number of messages to keep in each active conversation document.
            When this limit is exceeded, older messages will be archived.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Archive Buffer Size:</strong> The number of messages that must accumulate beyond the history limit before
            triggering an archiving operation. A larger buffer reduces the frequency of archiving operations.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MessageArchivalSettings;
